/**
 * API e hook del modulo ENISA di un report (srp-service).
 *   GET /srp/enisa-form/schema, GET|PUT /srp/reports/:id/enisa-form,
 *   GET /srp/reports/:id/enisa-form/export (file JSON per l'estensione Chrome).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createServiceClient } from '../../../api/client.js';
import { srpKeys } from '../hooks.js';
import type {
  EnisaFormSchema,
  EnisaFormState,
  EnisaFormValues,
  SaveEnisaFormResult,
} from './types.js';

const srpApi = createServiceClient('srp');

export function fetchEnisaFormSchema(): Promise<EnisaFormSchema> {
  return srpApi<EnisaFormSchema>('/srp/enisa-form/schema');
}

export function fetchEnisaForm(reportId: number): Promise<EnisaFormState> {
  return srpApi<EnisaFormState>(`/srp/reports/${reportId}/enisa-form`);
}

/** Salva la bozza; `revision` = revisione letta (0 se mai salvata). 409 se superata. */
export function saveEnisaForm(
  reportId: number,
  values: EnisaFormValues,
  revision: number,
): Promise<SaveEnisaFormResult> {
  return srpApi<SaveEnisaFormResult>(`/srp/reports/${reportId}/enisa-form`, {
    method: 'PUT',
    body: { values, revision },
  });
}

/** Documento di export (formato in docs/enisa-form-export.md). L'export è tracciato in audit. */
export function fetchEnisaExport(reportId: number): Promise<{ draftRevision: number }> {
  return srpApi<{ draftRevision: number }>(`/srp/reports/${reportId}/enisa-form/export`);
}

export const enisaFormKeys = {
  schema: () => ['srp', 'enisa-form-schema'] as const,
  form: (reportId: number) => ['srp', 'enisa-form', reportId] as const,
};

export function useEnisaFormSchema(enabled: boolean) {
  return useQuery({
    queryKey: enisaFormKeys.schema(),
    queryFn: fetchEnisaFormSchema,
    enabled,
    staleTime: Infinity,
  });
}

export function useEnisaForm(reportId: number, enabled: boolean) {
  return useQuery({
    queryKey: enisaFormKeys.form(reportId),
    queryFn: () => fetchEnisaForm(reportId),
    enabled: enabled && reportId > 0,
  });
}

/** Salvataggio: rilegge modulo, report (riepilogo bozza) e storico audit (voci per campo). */
export function useSaveEnisaForm(reportId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { values: EnisaFormValues; revision: number }) =>
      saveEnisaForm(reportId, input.values, input.revision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: enisaFormKeys.form(reportId) });
      void queryClient.invalidateQueries({ queryKey: srpKeys.report(reportId) });
      void queryClient.invalidateQueries({ queryKey: srpKeys.auditHistory(reportId) });
    },
  });
}
